<template>
    <v-card
        :flat="flat"
    >
        <v-card-title
            class="d-flex align-center"
            :class="isMobile ? 'justify-center' : ''"
        >
            <v-btn
                class="custom-button"
                type="button"
                color="accent"
                v-if="createButton"
                @click="$emit('create')"
                :disabled="fetching"
            >
                {{ createButtonLabel }}
            </v-btn>

            <v-spacer
                v-if="!isMobile"
            ></v-spacer>

            <slot name="additional-buttons"></slot>

            <v-tooltip
                bottom
                v-if="downloadButton"
            >
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        icon
                        :disabled="fetching"
                        @click="$emit('download')"
                    >
                        <v-icon>$mdiDownload</v-icon>
                    </v-btn>
                </template>
                <span>Download</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        icon
                        :disabled="fetching"
                        @click="fetchData"
                    >
                        <v-icon>$mdiAutorenew</v-icon>
                    </v-btn>
                </template>
                <span>Refresh</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn
                        @click.native.stop="resetFilters"
                        icon
                        v-show="showFiltersBadge"
                        :disabled="fetching"
                    >
                        <v-icon>
                            $mdiFilterRemoveOutline
                        </v-icon>
                    </v-btn>
                </template>
                <span>Clear Filters</span>
            </v-tooltip>

            <v-tooltip
                bottom
                v-if="filterButton"
            >
                <template v-slot:activator="{ on }">
                    <v-btn
                        @click.native.stop="toggleFilterDrawer"
                        v-on="on"
                        icon
                        :disabled="fetching"
                    >
                        <v-badge
                            :value="showFiltersBadge"
                            color="error"
                            left
                            overlap
                        >
                            <span
                                slot="badge"
                            >
                                {{ selectedFiltersCount }}
                            </span>
                            <v-icon>$mdiFilterVariant</v-icon>
                        </v-badge>
                    </v-btn>
                </template>
                <span>Filters</span>
            </v-tooltip>

            <v-text-field
                class="title-branding custom-input ml-3"
                v-model="searchQuery"
                label="Search"
                type="text"
                filled
                hide-details
                prepend-inner-icon="$mdiMagnify"
                :disabled="fetching"
                :class="isMobile ? 'mt-2' : ''"
            ></v-text-field>


        </v-card-title>
        <v-card-text>
            <v-data-table
                :headers="headers"
                :items="items"
                :options.sync="options"
                :server-items-length="totalItems"
                :loading="fetching"
                :footer-props="{
                    'items-per-page-options': $store.getters['application/getPerPage'],
                    'disable-items-per-page' : freeze,
                    'disable-pagination' : freeze,
                }"
                multi-sort
                :disable-filtering="freeze"
                :disable-pagination="freeze"
                :disable-sort="freeze"
                :mobile-breakpoint="breakpoint"
            >
                <template v-slot:body="{ items, isMobile }">
                    <tbody>
                    <tr
                        v-for="(item, index) in items"
                        :key="index"
                        :class="[isMobile ? 'v-data-table__mobile-table-row' : '', clickable ? 'pointer' : '', customClasses(item)]"
                        @click="triggerClickEvent(item)"
                    >
                        <template v-if="isMobile">
                            <slot
                                name="card"
                                v-bind:item="item"
                            >
                                <td
                                    class="v-data-table__mobile-row"
                                    v-for="(header, headerIndex) in headers"
                                    :key="headerIndex"
                                >
                                    <div class="v-data-table__mobile-row__header">
                                        {{ header.text }}
                                    </div>
                                    <div class="v-data-table__mobile-row__cell">
                                        {{ getCardValue(item, header.value) }}
                                    </div>
                                </td>
                            </slot>
                        </template>
                        <template v-else>
                            <slot
                                name="row"
                                v-bind:item="item"
                            ></slot>
                        </template>
                    </tr>
                    </tbody>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
    export default {
        name: "CustomTable",

        computed: {
            showFiltersBadge() {
                if (this.selectedFiltersCount === 'undefined') {
                    return false;
                }

                return this.selectedFiltersCount > 0;
            },

            selectedFiltersCount() {
                return this.$store.getters['table/getSelectedFiltersCount'];
            },

            isMobile() {
                return this.$vuetify.breakpoint.width < this.breakpoint;
            }
        },

        props: {
            'single-item-name': {
                type: String,
                required: true
            },
            'plural-item-name': {
                type: String,
                required: true
            },

            'data-url': {
                type: String,
                default: null
            },
            'additional-data': {
                type: Object,
                default: () => ({})
            },

            'headers': {
                type: Array,
                default: () => ([])
            },

            'freeze': {
                type: Boolean,
                default: false
            },

            'force-refresh': {
                type: Boolean,
                default: false
            },

            'clickable': {
                type: Boolean,
                default: false
            },

            'flat': {
                type: Boolean,
                default: false
            },

            'breakpoint': {
                type: Number,
                default: 768
            },

            'createButton': {
                type: Boolean,
                default: false
            },

            'createButtonLabel': {
                type: String,
                default: 'New'
            },

            'downloadButton': {
                type: Boolean,
                default: false
            },

            'customClasses': {
                type: Function,
                default: () => '',
            },

            'filterButton': {
                type: Boolean,
                default: true
            }
        },

        data() {
            return {
                searching: false,
                totalItems: 0,
                items: [],
                fetching: true,
                timeout: null,

                options: {
                    groupBy: [],
                    groupDesc: [],
                    itemsPerPage: 10,
                    multiSort: false,
                    mustSort: false,
                    page: 1,
                    sortBy: [],
                    sortDesc: [],
                },

                previousQuery: '',

                deleteDialog: false,
                deleteWaiting: false,
                deleteItem: null,
            }
        },

        watch: {
            'searchQuery'() {
                if (!this.fetching) {
                    this.fetchDataDelayed();
                }
                this.setQueryParameter(this.searchQuery);
            },
            'selectedFiltersCount'() {
                this.fetchDataDelayed();
            },
            'forceRefresh'() {
                if (!this.fetching) {
                    this.fetchData();
                }
            },
            'options'() {
                if (!this.fetching) {
                    this.fetchData();
                }
            },
        },

        mounted() {
            this.options.itemsPerPage = this.$store.getters['application/getPerPage'][0];

            this.checkUrl()
                .then(response => {
                    this.fetchData();
                })
                .catch(error => {

                });
        },

        methods: {
            triggerClickEvent(item) {
                this.$emit('click', item)
            },

            fetchDataDelayed() {
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.fetchData();
                }, 1000);
            },
            fetchData() {

                this.fetching = true;

                if (this.tableFilter.searchQuery !== this.previousQuery) {
                    this.options.page = 1;
                }

                this.setPageParameter(this.options.page);

                this.setSortByParameter(this.options.sortBy, this.options.sortDesc);

                this.setItemsPerPageParameter(this.options.itemsPerPage);


                let url = '/' + this.pluralItemName + '/searchPaginated';

                if (this.dataUrl) {
                    url = this.dataUrl;
                }

                window.axios.post(url + '?page=' + this.options.page, {
                    filter: this.tableFilter,
                    itemsPerPage: this.options.itemsPerPage,
                    sortBy: this.options.sortBy,
                    sortDesc: this.options.sortDesc,
                    ...this.additionalData
                }).then(response => {
                    this.items = response.data[this.pluralItemName].data;
                    this.totalItems = response.data[this.pluralItemName].total;
                    this.fetching = false;

                    this.previousQuery = this.tableFilter.searchQuery;
                });
            },

            getCardValue(item, key) {
                const keyDepth = key.split('.');

                let currentItem = '';

                for (let i = 0, len = keyDepth.length; i < len; i++) {
                    if (currentItem === '') {
                        if (typeof item[keyDepth[i]] == 'undefined') {
                            return '';
                        } else {
                            currentItem = item[keyDepth[i]];
                        }
                    } else {
                        if (typeof currentItem[keyDepth[i]] == 'undefined') {
                            return '';
                        } else {
                            currentItem = currentItem[keyDepth[i]];
                        }
                    }
                }

                return currentItem;
            },

            toggleFilterDrawer() {
                this.$store.commit('application/setFilterDrawerStatus', !this.$store.getters['application/getFilterDrawerStatus']);
            },
        }
    }
</script>
